/**
 * recoveryPlanCore.js — какой случай «том не держится» и чем его лечить.
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * [v2.0 FIXES]:
 *  - Случай B: mark: false (текстуры уничтожены, метка бесполезна)
 *  - Случай A: проверка activeViewports (нет вьюпортов → нечего метить)
 *  - verifyRecovery: раздельный учёт expected и unrestorable
 *  - JSDoc: все параметры документированы
 *  - Имена параметров: state и result вместо st и r
 *
 * Вынесено отдельно и без браузерных зависимостей: сама починка завязана на
 * Cornerstone и проверяется только на устройстве, а ВЫБОР средства — чистое
 * правило, и именно в нём была ошибка. Прежние попытки лечили один случай из
 * трёх и потому помогали через раз.
 *
 * Три причины, по которым после сворачивания браузера окно чернеет:
 *
 *   A. Текстура вытеснена из видеопамяти, контекст WebGL жив.
 *      Лечится пометкой imageData «грязным»: на следующем рендере зальётся.
 *
 *   B. Контекст WebGL потерян (было событие webglcontextlost).
 *      В видеопамяти умерли все объекты рендер-окна, заливать некуда.
 *      Нужна переустановка томов во вьюпорты — она пересоздаёт актёров.
 *
 *   C. Том выгружен из кэша Cornerstone «Стражем памяти».
 *      Нет ни текстуры, ни данных: том надо создать заново из imageIds.
 *      Если imageIds не сохранены — восстановить нечем, надо сказать вслух.
 *
 * Порядок проверки важен: C перекрывает B, а B перекрывает A. Если тома нет
 * в кэше, бессмысленно и метить, и переустанавливать — нечего.
 */

/**
 * План восстановления после потери томов.
 *
 * @param {object} [state={}]
 * @param {string[]} [state.shownVolumeIds] — тома, показанные во вьюпортах
 * @param {string[]} [state.missingVolumeIds] — подмножество shownVolumeIds, которых нет в кэше
 * @param {boolean} [state.glLostSince] — было ли событие потери контекста с прошлого восстановления
 * @param {boolean} [state.haveLastLoad] — сохранены ли imageIds последней загрузки
 * @param {boolean} [state.contextStillLost] — контекст потерян ПРЯМО СЕЙЧАС (webglcontextrestored ещё не пришло)
 * @param {number} [state.activeViewports] — количество активных 2D/3D вьюпортов
 * @returns {{
 *   case: string,
 *   action: string,
 *   mark: boolean,
 *   resetVolumes: boolean,
 *   reload: boolean,
 *   wait: boolean,
 *   tellUser: string|null
 * }}
 */
export function planRecovery(state = {}) {
  const shown = state.shownVolumeIds || [];
  const missing = state.missingVolumeIds || [];
  // [исправлено] Отсутствие поля означает «неизвестно», а не «вьюпортов нет».
  // При `|| 0` любой вызов без нового поля молча возвращал «нет вьюпортов»,
  // и восстановление не делало НИЧЕГО — тихий отказ хуже явной ошибки.
  const activeViewports = state.activeViewports === undefined || state.activeViewports === null
    ? Infinity
    : Number(state.activeViewports);

  // Контекст ещё не восстановлен — рисовать некуда, ждём webglcontextrestored.
  if (state.contextStillLost) {
    return {
      case: 'ждём',
      action: 'контекст GPU ещё не восстановлен',
      mark: false,
      resetVolumes: false,
      reload: false,
      wait: true,
      tellUser: null,
    };
  }

  // Случай C: том выгружен из кэша — надо перезагрузить из imageIds
  if (missing.length) {
    if (state.haveLastLoad) {
      return {
        case: 'C',
        action: 'перезагрузить том из imageIds',
        mark: false,
        resetVolumes: false,
        reload: true,
        wait: false,
        tellUser: 'Восстанавливаю том…',
      };
    }
    return {
      case: 'C',
      action: 'нечем восстановить — нужна повторная загрузка серии',
      mark: false,
      resetVolumes: false,
      reload: false,
      wait: false,
      tellUser: 'Том выгружен из памяти — откройте серию заново',
    };
  }

  // Случай B: контекст WebGL потерян — текстуры уничтожены, актёры мертвы.
  // mark: false потому что метка «грязный» бесполезна: заливать некуда.
  // resetVolumes: true пересоздаст актёров с новыми текстурами.
  if (state.glLostSince) {
    return {
      case: 'B',
      action: 'переустановить тома во вьюпорты',
      mark: false,
      resetVolumes: true,
      reload: false,
      wait: false,
      tellUser: null,
    };
  }

  // Нет томов для восстановления
  if (!shown.length) {
    return {
      case: 'нет томов',
      action: 'нечего восстанавливать',
      mark: false,
      resetVolumes: false,
      reload: false,
      wait: false,
      tellUser: null,
    };
  }

  // Нет активных вьюпортов — негде пометить текстуры
  if (!activeViewports) {
    return {
      case: 'нет вьюпортов',
      action: 'некуда выводить — вьюпорты не найдены',
      mark: false,
      resetVolumes: false,
      reload: false,
      wait: false,
      tellUser: null,
    };
  }

  // Случай A: текстура вытеснена из VRAM, контекст жив.
  // Помечаем imageData «грязным» — на следующем рендере зальётся.
  return {
    case: 'A',
    action: 'пометить текстуры к перезаливу',
    mark: true,
    resetVolumes: false,
    reload: false,
    wait: false,
    tellUser: null,
  };
}

/**
 * Проверка РЕЗУЛЬТАТА восстановления, а не намерения. [7.0.60]
 *
 * Прежний отчёт писал «Перезагружен из исходных снимков» сразу после вызова
 * загрузки — то есть докладывал о том, что сделал попытку. На устройстве это
 * обернулось так: пропало три тома, вернулся один, а в отчёте стояло, что всё
 * восстановлено. Врач присылает такой отчёт, я читаю «случай C закрыт» — и
 * вопрос считается решённым, хотя экран у него по-прежнему чёрный.
 *
 * Поэтому теперь после починки пересчитываются тома и актёры во вьюпортах, и
 * в журнал идёт то, что получилось на самом деле.
 *
 * @param {object} [result={}]
 * @param {string[]} [result.expected] — тома, которые должны быть на месте после починки
 * @param {string[]} [result.present] — тома, реально найденные в кэше
 * @param {string[]} [result.rendered] — тома, у которых есть актёр во вьюпорте
 *   (одного кэша мало: данные могут лежать, а на экране их не будет)
 * @param {string[]} [result.unrestorable] — заведомо невосстановимые
 *   (исходных снимков нет вовсе, imageIds не сохранены)
 * @returns {{
 *   ok: boolean,
 *   restored: string[],
 *   failed: string[],
 *   blind: string[],
 *   unrestorable: string[],
 *   text: string
 * }}
 */
export function verifyRecovery(result = {}) {
  const expected = (result.expected || []).map(String);
  const present = new Set((result.present || []).map(String));
  const rendered = new Set((result.rendered || []).map(String));
  const unrestorable = (result.unrestorable || []).map(String);

  // Тома, которые и в кэше, и имеют актёра во вьюпорте
  const restored = expected.filter(id => present.has(id) && rendered.has(id));

  // Данные вернулись, а на экран не попали — отдельная беда, и молчать о ней
  // нельзя: именно так выглядит «чёрное окно при живом томе».
  const blind = expected.filter(id => present.has(id) && !rendered.has(id));

  // Не вернулись в кэш вообще
  const failed = expected.filter(id => !present.has(id));

  const ok = failed.length === 0 && blind.length === 0 && unrestorable.length === 0;

  let text;
  if (!expected.length && !unrestorable.length) {
    text = 'восстанавливать было нечего';
  } else if (ok) {
    text = `восстановлено ${restored.length} из ${expected.length}`;
  } else {
    const parts = [`восстановлено ${restored.length} из ${expected.length}`];

    if (failed.length) {
      parts.push(`не вернулись: ${failed.length}`);
    }
    if (blind.length) {
      parts.push(`вернулись, но не показаны: ${blind.length}`);
    }
    if (unrestorable.length) {
      parts.push(`нечем восстановить: ${unrestorable.length}`);
    }

    text = parts.join('; ');
  }

  return {
    ok,
    restored,
    failed,
    blind,
    unrestorable,
    text,
  };
}

export default { planRecovery, verifyRecovery };